import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PhoneIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  TagIcon,
  CalendarIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';

interface CRMContact {
  _id: string;
  companyName: string;
  stockSymbol?: string;
  exchange?: string;
  industry?: string;
  sector?: string;
  marketCap?: number;
  employees?: number;
  revenue?: number;
  website?: string;
  description?: string;
  headquarters?: {
    city?: string;
    state?: string;
    country?: string;
  };
  contacts: Array<{
    name?: string;
    title?: string;
    email?: string;
    phone?: string;
    isPrimary: boolean;
    userId?: string;
  }>;
  dealStatus: string;
  dealValue?: number;
  priority: string;
  assignedTo?: {
    _id: string;
    name: string;
    email: string;
  };
  activities: Array<{
    type: string;
    timestamp: Date;
    description: string;
    details?: any;
    performedBy?: {
      name: string;
    };
  }>;
  notes: Array<{
    _id: string;
    content: string;
    author: {
      name: string;
    };
    timestamp: Date;
    type: string;
  }>;
  files: Array<{
    _id: string;
    filename: string;
    type: string;
    uploadDate: Date;
    size: number;
  }>;
  tags: string[];
  firstVisit?: Date;
  lastVisit?: Date;
  totalVisits: number;
  createdAt: Date;
  updatedAt: Date;
}

const CRM: React.FC = () => {
  const [contacts, setContacts] = useState<CRMContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<CRMContact | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    dealStatus: '',
    priority: '',
    assignedTo: ''
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showNewNote, setShowNewNote] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', type: 'general' });
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState({ subject: '', message: '', priority: 'normal' });

  useEffect(() => {
    fetchContacts();
  }, [filter, sortBy, sortOrder]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get('/api/crm/contacts', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchTerm,
          dealStatus: filter.dealStatus,
          priority: filter.priority,
          assignedTo: filter.assignedTo,
          sortBy,
          sortOrder
        }
      });
      
      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactDetails = async (contactId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/crm/contact/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedContact(response.data.contact);
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }
  };

  const addNote = async () => {
    if (!selectedContact || !newNote.content) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/crm/contact/${selectedContact._id}/note`,
        newNote,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh contact details
      fetchContactDetails(selectedContact._id);
      setNewNote({ content: '', type: 'general' });
      setShowNewNote(false);
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const sendMessage = async () => {
    if (!selectedContact || !message.subject || !message.message) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/crm/contact/${selectedContact._id}/message`,
        message,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh contact details
      fetchContactDetails(selectedContact._id);
      setMessage({ subject: '', message: '', priority: 'normal' });
      setShowMessage(false);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const updateContactStatus = async (contactId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/crm/contact/${contactId}`,
        { dealStatus: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh contacts
      fetchContacts();
      if (selectedContact && selectedContact._id === contactId) {
        fetchContactDetails(contactId);
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      visitor: 'bg-gray-100 text-gray-800',
      lead: 'bg-blue-100 text-blue-800',
      qualified: 'bg-purple-100 text-purple-800',
      negotiating: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      churned: 'bg-red-100 text-red-800',
      inactive: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'text-gray-500',
      medium: 'text-blue-500',
      high: 'text-orange-500',
      urgent: 'text-red-500'
    };
    return colors[priority] || 'text-gray-500';
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Contacts List */}
      <div className={`${selectedContact ? 'w-1/3' : 'w-full'} border-r border-gray-200 overflow-y-auto`}>
        <div className="p-4 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchContacts()}
                className="pl-10 pr-4 py-2 w-full border rounded-lg"
              />
            </div>
            <button className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="flex gap-2">
            <select
              value={filter.dealStatus}
              onChange={(e) => setFilter({ ...filter, dealStatus: e.target.value })}
              className="px-3 py-1 text-sm border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="visitor">Visitor</option>
              <option value="lead">Lead</option>
              <option value="qualified">Qualified</option>
              <option value="negotiating">Negotiating</option>
              <option value="active">Active</option>
              <option value="churned">Churned</option>
            </select>
            
            <select
              value={filter.priority}
              onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
              className="px-3 py-1 text-sm border rounded-lg"
            >
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            
            <button
              onClick={() => {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              }}
              className="p-1 text-gray-600 hover:text-gray-800"
            >
              {sortOrder === 'asc' ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {contacts.map((contact) => (
            <motion.div
              key={contact._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`p-4 hover:bg-gray-50 cursor-pointer ${
                selectedContact?._id === contact._id ? 'bg-blue-50' : ''
              }`}
              onClick={() => fetchContactDetails(contact._id)}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">{contact.companyName}</h3>
                  {contact.stockSymbol && (
                    <p className="text-sm text-gray-500">{contact.stockSymbol}</p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.dealStatus)}`}>
                  {contact.dealStatus}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className={`${getPriorityColor(contact.priority)}`}>
                    {contact.priority}
                  </span>
                  {contact.dealValue && (
                    <span className="text-gray-600">{formatCurrency(contact.dealValue)}</span>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  {formatDistanceToNow(new Date(contact.updatedAt))} ago
                </span>
              </div>
              
              {contact.activities.length > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  Last: {contact.activities[0].type.replace('_', ' ')}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Details */}
      {selectedContact && (
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="bg-white border-b p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedContact.companyName}</h2>
                {selectedContact.stockSymbol && (
                  <p className="text-lg text-gray-600">
                    {selectedContact.stockSymbol} 
                    {selectedContact.exchange && ` • ${selectedContact.exchange}`}
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedContact(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                ×
              </button>
            </div>
            
            <div className="mt-4 flex gap-4">
              <select
                value={selectedContact.dealStatus}
                onChange={(e) => updateContactStatus(selectedContact._id, e.target.value)}
                className={`px-4 py-2 rounded-lg font-medium ${getStatusColor(selectedContact.dealStatus)}`}
              >
                <option value="visitor">Visitor</option>
                <option value="lead">Lead</option>
                <option value="qualified">Qualified</option>
                <option value="negotiating">Negotiating</option>
                <option value="active">Active</option>
                <option value="churned">Churned</option>
                <option value="inactive">Inactive</option>
              </select>
              
              <button
                onClick={() => setShowMessage(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <EnvelopeIcon className="h-4 w-4" />
                Send Message
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Company Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BuildingOfficeIcon className="h-5 w-5" />
                Company Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Industry:</span>
                  <p className="font-medium">{selectedContact.industry || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Sector:</span>
                  <p className="font-medium">{selectedContact.sector || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Market Cap:</span>
                  <p className="font-medium">{formatCurrency(selectedContact.marketCap)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Revenue:</span>
                  <p className="font-medium">{formatCurrency(selectedContact.revenue)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Employees:</span>
                  <p className="font-medium">{selectedContact.employees || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Website:</span>
                  <p className="font-medium">
                    {selectedContact.website ? (
                      <a href={selectedContact.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                        {selectedContact.website}
                      </a>
                    ) : '-'}
                  </p>
                </div>
              </div>
              {selectedContact.headquarters && (
                <div className="mt-4">
                  <span className="text-gray-500 text-sm">Headquarters:</span>
                  <p className="font-medium">
                    {[
                      selectedContact.headquarters.city,
                      selectedContact.headquarters.state,
                      selectedContact.headquarters.country
                    ].filter(Boolean).join(', ') || '-'}
                  </p>
                </div>
              )}
            </div>

            {/* Contacts */}
            {selectedContact.contacts.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5" />
                  Contacts
                </h3>
                <div className="space-y-3">
                  {selectedContact.contacts.map((contact, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{contact.name || 'Unknown'}</p>
                        {contact.title && <p className="text-sm text-gray-600">{contact.title}</p>}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        {contact.email && (
                          <a href={`mailto:${contact.email}`} className="text-primary-600 hover:text-primary-700 flex items-center gap-1">
                            <EnvelopeIcon className="h-4 w-4" />
                            {contact.email}
                          </a>
                        )}
                        {contact.phone && (
                          <a href={`tel:${contact.phone}`} className="text-gray-600 hover:text-gray-700 flex items-center gap-1">
                            <PhoneIcon className="h-4 w-4" />
                            {contact.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Activity Timeline
              </h3>
              <div className="space-y-4">
                {selectedContact.activities.slice(0, 10).map((activity, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-primary-600 rounded-full"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900">
                          {activity.type.replace(/_/g, ' ').charAt(0).toUpperCase() + activity.type.replace(/_/g, ' ').slice(1)}
                        </p>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(activity.timestamp))} ago
                        </span>
                      </div>
                      {activity.description && (
                        <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                      )}
                      {activity.performedBy && (
                        <p className="text-xs text-gray-500 mt-1">by {activity.performedBy.name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  Notes
                </h3>
                <button
                  onClick={() => setShowNewNote(true)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Add Note
                </button>
              </div>
              
              {showNewNote && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <textarea
                    value={newNote.content}
                    onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                    placeholder="Add a note..."
                    className="w-full p-3 border rounded-lg resize-none"
                    rows={3}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <select
                      value={newNote.type}
                      onChange={(e) => setNewNote({ ...newNote, type: e.target.value })}
                      className="px-3 py-1 text-sm border rounded-lg"
                    >
                      <option value="general">General</option>
                      <option value="call">Call</option>
                      <option value="meeting">Meeting</option>
                      <option value="email">Email</option>
                      <option value="internal">Internal</option>
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowNewNote(false)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addNote}
                        className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      >
                        Save Note
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                {selectedContact.notes.map((note) => (
                  <div key={note._id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-medium text-gray-500 uppercase">{note.type}</span>
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(note.timestamp))} ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{note.content}</p>
                    <p className="text-xs text-gray-500 mt-1">by {note.author.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Files */}
            {selectedContact.files.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <PaperClipIcon className="h-5 w-5" />
                  Files
                </h3>
                <div className="space-y-2">
                  {selectedContact.files.map((file) => (
                    <div key={file._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <PaperClipIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-sm">{file.filename}</p>
                          <p className="text-xs text-gray-500">
                            {file.type} • {Math.round(file.size / 1024)}KB
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {format(new Date(file.uploadDate), 'MMM d, yyyy')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {showMessage && selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Send Message to {selectedContact.companyName}</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={message.subject}
                    onChange={(e) => setMessage({ ...message, subject: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={message.message}
                    onChange={(e) => setMessage({ ...message, message: e.target.value })}
                    rows={5}
                    className="w-full px-3 py-2 border rounded-lg resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={message.priority}
                    onChange={(e) => setMessage({ ...message, priority: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowMessage(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={sendMessage}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRM; 